import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
/**
 * Sequence — FTUE demo (3 essais progressive intro).
 */
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { COLOR_PALETTE } from './params';
const DEMO_STEPS = [
    {
        idx: 1,
        titleKey: 'sequence.demo.step1_title',
        hintKey: 'sequence.demo.step1_hint',
        mode: 'digits',
        items: [3, 7, 1],
        itemDurationMs: 1200,
    },
    {
        idx: 2,
        titleKey: 'sequence.demo.step2_title',
        hintKey: 'sequence.demo.step2_hint',
        mode: 'digits',
        items: [4, 9, 2, 6],
        itemDurationMs: 1000,
    },
    {
        idx: 3,
        titleKey: 'sequence.demo.step3_title',
        hintKey: 'sequence.demo.step3_hint',
        mode: 'colors',
        items: [0, 3, 5, 1],
        itemDurationMs: 1000,
    },
];
const STAGE_SIZE = 280;
const COLOR_DOT_SIZE = 80;
const GAP_MS = 200;
export function Demo({ level: _level, onComplete, onSkip }) {
    const { t } = useTranslation(['games', 'common']);
    const [stepIdx, setStepIdx] = useState(0);
    const [phase, setPhase] = useState('idle');
    const [currentItemIdx, setCurrentItemIdx] = useState(-1);
    const [picked, setPicked] = useState([]);
    const [verdict, setVerdict] = useState(null);
    const timersRef = useRef([]);
    const step = DEMO_STEPS[stepIdx];
    const isLast = stepIdx === DEMO_STEPS.length - 1;
    const n = step.items.length;
    const clearTimers = () => {
        while (timersRef.current.length > 0) {
            const tt = timersRef.current.pop();
            if (tt !== undefined)
                clearTimeout(tt);
        }
    };
    useEffect(() => () => clearTimers(), []);
    const startSequence = () => {
        clearTimers();
        setPicked([]);
        setVerdict(null);
        setPhase('showing');
        let acc = 0;
        step.items.forEach((_, i) => {
            timersRef.current.push(setTimeout(() => setCurrentItemIdx(i), acc));
            acc += step.itemDurationMs;
            timersRef.current.push(setTimeout(() => setCurrentItemIdx(-1), acc));
            acc += GAP_MS;
        });
        timersRef.current.push(setTimeout(() => {
            setPhase('awaiting');
        }, acc));
    };
    const handlePick = (item) => {
        if (phase !== 'awaiting')
            return;
        const newPicked = [...picked, item];
        setPicked(newPicked);
        if (newPicked.length >= n) {
            const correct = newPicked.every((p, i) => p === step.items[i]);
            setVerdict(correct ? 'correct' : 'incorrect');
            setPhase('feedback');
        }
    };
    const handleNext = () => {
        if (isLast) {
            onComplete();
            return;
        }
        setStepIdx((i) => i + 1);
        setPhase('idle');
        setCurrentItemIdx(-1);
        setPicked([]);
        setVerdict(null);
    };
    const currentItem = phase === 'showing' && currentItemIdx >= 0 && currentItemIdx < step.items.length
        ? step.items[currentItemIdx]
        : null;
    return (_jsxs("div", { className: "flex flex-1 flex-col items-center gap-s4 py-s4 text-center", children: [_jsx("p", { className: "font-mono text-[9.5px] uppercase tracking-[0.30em] text-cyan-text-faint", children: t('common:demo.eyebrow_with_master', { master: t('sequence.demo.master') }) }), _jsx("h2", { className: "font-display text-[22px] font-medium leading-7 tracking-cy-display text-cyan-text", children: t(step.titleKey) }), _jsx("p", { className: "max-w-[320px] text-[13.5px] text-cyan-text-secondary", children: t(step.hintKey) }), _jsx("div", { className: "flex items-center justify-center", style: { width: STAGE_SIZE, height: STAGE_SIZE, background: '#1a1a1a' }, children: currentItem === null ? (_jsx("span", { className: "font-display text-[40px] text-cyan-surface opacity-30", children: "\u00B7" })) : step.mode === 'digits' ? (_jsx("span", { className: "font-display text-cyan-surface", style: { fontSize: 96, lineHeight: 1 }, children: currentItem })) : (_jsx("span", { "aria-hidden": true, style: {
                        display: 'inline-block',
                        width: COLOR_DOT_SIZE,
                        height: COLOR_DOT_SIZE,
                        borderRadius: '50%',
                        background: COLOR_PALETTE[currentItem % COLOR_PALETTE.length].bg,
                        boxShadow: '0 0 36px rgba(255,255,255,0.18)',
                    } })) }), phase === 'awaiting' && (_jsxs(_Fragment, { children: [_jsx("p", { className: "text-[12px] text-cyan-text-secondary", children: t('sequence.demo.your_turn', { picks: picked.length, n }) }), step.mode === 'digits' ? (_jsxs("div", { className: "grid gap-s2", style: { gridTemplateColumns: 'repeat(3, 56px)' }, children: [[1, 2, 3, 4, 5, 6, 7, 8, 9].map((d) => (_jsx("button", { type: "button", onClick: () => handlePick(d), className: "flex h-[56px] w-[56px] items-center justify-center bg-cyan-core font-display text-[22px] font-medium text-cyan-surface transition active:scale-95 hover:bg-cyan-network", "aria-label": t('sequence.digit_aria', { digit: d }), children: d }, `demo-d-${d}`))), _jsx("div", {}), _jsx("button", { type: "button", onClick: () => handlePick(0), className: "flex h-[56px] w-[56px] items-center justify-center bg-cyan-core font-display text-[22px] font-medium text-cyan-surface transition active:scale-95 hover:bg-cyan-network", "aria-label": t('sequence.digit_aria', { digit: 0 }), children: "0" }, "demo-d-0"), _jsx("div", {})] })) : (_jsx("div", { className: "grid gap-s2", style: { gridTemplateColumns: 'repeat(4, 52px)' }, children: COLOR_PALETTE.map((c, idx) => (_jsx("button", { type: "button", onClick: () => handlePick(idx), "aria-label": t('sequence.color_aria', { name: c.label }), className: "h-[52px] w-[52px] rounded-full hover:scale-110 active:scale-95", style: { background: c.bg, boxShadow: '0 2px 6px rgba(0,0,0,0.18)' } }, `demo-c-${c.key}`))) }))] })), phase === 'feedback' && (_jsxs("div", { className: "flex flex-col items-center gap-s2", children: [_jsx("span", { className: [
                            'font-display text-[16px] font-medium',
                            verdict === 'correct' ? 'text-cyan-core' : 'text-cyan-media',
                        ].join(' '), children: verdict === 'correct'
                            ? t('sequence.demo.feedback_correct')
                            : t('sequence.demo.feedback_incorrect') }), _jsxs("div", { className: "flex items-center gap-s2 text-[11px] text-cyan-text-secondary", children: [_jsx("span", { children: t('sequence.demo.truth_label') }), _jsx("span", { className: "font-mono", children: step.items.join(' · ') })] })] })), _jsxs("div", { className: "flex flex-col items-stretch gap-s3 self-stretch", children: [phase === 'idle' && (_jsx("button", { type: "button", onClick: startSequence, className: "inline-flex h-12 w-full items-center justify-center bg-cyan-core font-display text-[14px] font-medium tracking-[-0.01em] text-cyan-surface transition active:scale-[0.985] hover:bg-cyan-network", children: t('sequence.demo.launch') })), phase === 'showing' && (_jsx("span", { className: "font-display text-[14px] text-cyan-network", children: t('sequence.demo.watch') })), phase === 'feedback' && (_jsxs(_Fragment, { children: [_jsx("button", { type: "button", onClick: handleNext, className: "inline-flex h-12 w-full items-center justify-center bg-cyan-core font-display text-[14px] font-medium tracking-[-0.01em] text-cyan-surface transition active:scale-[0.985] hover:bg-cyan-network", children: isLast ? t('common:actions.start_session') : t('common:actions.next_trial') }), _jsx("button", { type: "button", onClick: startSequence, className: "border border-cyan-border-faint px-s4 py-s2 text-[12px] text-cyan-text-secondary hover:border-cyan-border", children: t('sequence.demo.replay') })] })), _jsx("button", { type: "button", onClick: onSkip, className: "text-[12px] text-cyan-text-secondary underline-offset-4 hover:underline", children: t('common:actions.skip_demo') })] })] }));
}
//# sourceMappingURL=demo.js.map